import React from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Link } from 'react-router-dom';
import Hero from '../Components/Section1-Main';
import Badges from '../Components/Section-Cards';
import CarouselComponent from '../Components/Carousel.jsx';
import '../index.css';
import FAQComponent from '../Components/FAQ';
import OpeningTimes from '../Components/2-col-section-OP';
import ServiceBox from '../Components/serviceList.jsx';
import BlogSlider from '../Components/BlogSlider.jsx';
import { Suspense } from 'react';
import WavyLine from '../Components/wavyline';
import SEOHead from '../Components/SEOHead';
import { FaArrowRight } from 'react-icons/fa';

function MainPage() {

  const { scrollYProgress } = useScroll();
  
  // Parallax transforms
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -300]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -150]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -200]);
  
  // Smooth spring animations
  const springY1 = useSpring(y1, { stiffness: 100, damping: 30 });
  const springY2 = useSpring(y2, { stiffness: 100, damping: 30 });
  const springY3 = useSpring(y3, { stiffness: 100, damping: 30 });

  // Scroll progress indicator
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });



  return (
    <>
      <SEOHead 
        title="Drosh The Pod - Podiatry Services Manchester | Home Visits"
        description="Expert podiatry services in Manchester with home visits. Professional foot care, foot pain treatment, nail treatment, fungal nail management, and corn and callus removal. Book your appointment today."
        keywords="podiatry manchester, home visit podiatrist, foot care manchester, foot pain treatment, nail treatment, fungal nail management, corn removal, callus removal, professional podiatry"
        canonical="https://www.droshpod.com/"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "MedicalBusiness",
          "name": "Drosh The Pod",
          "description": "Professional podiatry services in Manchester with home visits",
          "url": "https://www.droshpod.com/",
          "telephone": "07809 267586",
          "email": "info@droshpod.com",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Manchester",
            "addressRegion": "Greater Manchester",
            "addressCountry": "UK"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "53.4808",
            "longitude": "-2.2426"
          },
          "openingHours": "Mo-Fr 09:00-17:00",
          "medicalSpecialty": "Podiatry",
          "serviceType": [
            "Foot Pain Treatment",
            "Nail Treatment",
            "Fungal Nail Management",
            "Corn and Callus Removal"
          ]
        }}
      />
      <main className="relative overflow-hidden">
      {/* Scroll Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-secondary to-primary origin-left z-50"
        style={{ scaleX }}
      />

      {/* Floating Background Elements */}
      <motion.div 
        className="fixed inset-0 pointer-events-none z-0"
        style={{ y: springY1 }}
      >
        <div className="absolute top-20 left-10 w-20 h-20 bg-primary/10 rounded-full blur-xl"></div>
        <div className="absolute top-40 right-20 w-16 h-16 bg-secondary/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-40 left-1/4 w-24 h-24 bg-primary/5 rounded-full blur-xl"></div>
      </motion.div>

      {/* Parallax Background Elements */}
      <motion.div 
        className="absolute inset-0 pointer-events-none"
        style={{ y: springY2 }}
      >
        <div className="absolute top-1/4 right-10 w-32 h-32 border border-primary/20 rounded-full"></div>
        <div className="absolute bottom-1/3 left-10 w-24 h-24 border border-secondary/20 rounded-full"></div>
      </motion.div>

      {/* Wavy Background with Parallax */}
      <motion.div 
        className="absolute inset-0 top-1/2 overflow-hidden -z-10"
        style={{ y: springY3 }}
      >
        <WavyLine />
      </motion.div>

      {/* Interactive Scroll Indicator */}
      <motion.div
        className="fixed right-6 top-1/2 transform -translate-y-1/2 z-40 hidden lg:block"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1 }}
      >
        <div className="flex flex-col items-center space-y-2">
          <div className="w-1 h-20 bg-gray-300 rounded-full relative">
            <motion.div
              className="absolute top-0 left-0 w-full bg-gradient-to-b from-secondary to-primary rounded-full"
              style={{ scaleY: scrollYProgress }}
            />
          </div>
        </div>
      </motion.div>

      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full"
          />
        </div>
      }>
        {/* Original Homepage Content */}
          {/* Hero Section with Enhanced Animation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <Hero />
          </motion.div>

          {/* Badges Section with Scroll Animation */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <Badges />
          </motion.div>

          {/* Service Box with Staggered Animation */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <ServiceBox />
          </motion.div>

          {/* Lunula Laser Promotional Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: "-100px" }}
            className="container mx-auto px-4 py-4 md:py-6 relative z-10"
          >
            <Link 
              to="/lunular-laser"
              className="block group"
            >
              <div className="relative bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary/30">
                <div className="p-6 md:p-8">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex-1 text-center md:text-left">
                      <p className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-medium">
                        Premium Treatment
                      </p>
                      <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-2">
                        Lunula Laser Treatment
                      </h2>
                      <p className="text-base md:text-lg text-gray-600 mb-4 max-w-2xl">
                        Painless, effective treatment for fungal nails. Grow new, healthy nails with our advanced laser technology.
                      </p>
                      <div className="flex justify-center md:justify-start">
                        <span className="text-primary font-medium text-sm inline-flex items-center group-hover:gap-2 transition-all duration-300">
                          Learn more
                          <FaArrowRight className="ml-1 text-xs group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>

          {/* H1 and Description Section - Below Hero for SEO (Subtle) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: "-50px" }}
            className="container mx-auto px-4 py-6"
          >
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-700 mb-3 text-center opacity-90">
              Professional Podiatry Services in Manchester
            </h1>
            <p className="text-base md:text-lg text-gray-500 text-center max-w-3xl mx-auto leading-relaxed">
              Expert home visit podiatry services across Manchester. Specialising in foot pain treatment, nail treatment, fungal nail management, and corn and callus removal. Registered podiatrist providing professional foot health solutions.
            </p>
          </motion.div>
          {/* Carousel with Enhanced Interaction */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <CarouselComponent />
          </motion.div>

          {/* Opening Times with Slide Animation */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <OpeningTimes />
          </motion.div>

          {/* FAQ with Accordion Animation */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <FAQComponent />
          </motion.div>

          {/* Blog Slider with Enhanced Animation */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            <BlogSlider />
          </motion.div>
      </Suspense>

      {/* Floating Action Button */}
      <motion.button
        className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-secondary to-primary text-white rounded-full shadow-lg hover:shadow-xl transition-shadow duration-300 z-40 flex items-center justify-center"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2, type: "spring", stiffness: 200 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </motion.button>
    </main>
    </>
  );
}

export default MainPage;